import type { AutorizaceSluzbaAutorizaceDto } from '@gov-nx/api/generated/autorizace.types';

/**
 * Authorization level configured for a service, or `undefined` when the service
 * is not configured for authorization at all.
 */
export type AuthorizationLevel = AutorizaceSluzbaAutorizaceDto['typAutorizace'];

/**
 * The single source of truth for what each authorization level means, so the
 * `'VYSOKA'` / `'NIZKA'` / `!== undefined` comparisons don't get spread across
 * presentation and runtime code with no way to see they line up.
 *
 * Invariant — the only level that triggers the post-submit authorization
 * redirect is `'VYSOKA'`:
 * - `isHighAuthorization` → drives the runtime handoff (`onInitAuthorization`).
 * - `isLowAuthorization`  → drives the explicit consent checkbox, no redirect.
 * - `isAuthorizationEnforced` → drives the "Autorizovat a …" button prefix.
 *
 * Because the runtime redirect is gated on `isHighAuthorization`, a stray
 * `x-autorizaceid` returned for a non-enforced service (level `undefined`)
 * never pushes the user into the authorization flow — it falls through to the
 * normal success path.
 */
export const isHighAuthorization = (level: AuthorizationLevel): boolean =>
	level === 'VYSOKA';

export const isLowAuthorization = (level: AuthorizationLevel): boolean =>
	level === 'NIZKA';

export const isAuthorizationEnforced = (level: AuthorizationLevel): boolean =>
	level !== undefined;
